import { FieldInputProps, useField } from "formik";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button, FormControl, FormGroup, FormLabel, ListGroup, ListGroupItem, Spinner } from "react-bootstrap";
import { WithTranslation } from "react-i18next";
import useSWR from "swr";
import { MerchantAdminApi } from "../../Api";
import i18n from "../../i18n";
import { IUserSessionStore } from "../../models/UserSessionStore";
import { ServerCity } from "../../models/server/ServerCity.js";
import { colors } from "../../theme/colors";
import "./CreateUpdateVenueScreen.scss"; // Keep if styles are still relevant
import { useDebounceValue } from "./utils"; // Assuming this utility hook exists and works as in EditLocationModel

interface CityFieldProps extends WithTranslation {
  api?: MerchantAdminApi;
  userSessionStore?: IUserSessionStore;
  fieldName?: string;
}

function stdFieldName<T>(field: FieldInputProps<T>) {
  return field.name.replace(/\[\d+\]/g, '')
}

const CityField = (props: CityFieldProps) => {
  const { t, api, fieldName } = props;
  const concreteFieldName = fieldName ?? "locations[0]";

  const [cityField, cityMeta, cityHelper] = useField<string>(`${concreteFieldName}.city`);
  const [countryField, countryMeta, countryHelper] = useField<string>(`${concreteFieldName}.country`);
  const [cityIdField, cityIdMeta, cityIdHelper] = useField<string>(`${concreteFieldName}.cityId`); // Used cityIdField from context

  const [showSearch, setShowSearch] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useDebounceValue(searchValue, 500);

  // Removed useState for selectedCity
  const containerRef = useRef<HTMLDivElement>(null);
  const languageCode = i18n.languages[0] || "en";

  // Removed useEffect that synced Formik fields to selectedCity state
  const citySearcher = useCallback(async (currentSearchValue: string): Promise<ServerCity[]> => {
    if (!api || currentSearchValue.length < 2) return [];
    const orgCountryId = props.userSessionStore?.currentOrganization?.countryId.id;
    const result = await api.searchCities(currentSearchValue, languageCode, orgCountryId);
    const allCities: ServerCity[] = result?.payload || [];
    return allCities;
  }, [api, languageCode]);

  const { data: cityResults, error: citySearchApiError, isValidating: isLoadingCities } = useSWR(
    debouncedSearchValue && debouncedSearchValue.length >= 2 ? [debouncedSearchValue, "searchCities"] : null,
    () => citySearcher(debouncedSearchValue),
    { revalidateOnFocus: false }
  );

  const handleSelectCity = (city: ServerCity) => {
    // Update Formik fields directly
    cityHelper.setValue(city.name);
    countryHelper.setValue(city.country);
    cityIdHelper.setValue(city.id);

    setShowSearch(false);
    setSearchValue("");
    setDebouncedSearchValue("");
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSearch(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [containerRef]);

  return (
    <div ref={containerRef} className="mb-3">
      <FormLabel className="textInputLabel">{t(`manageVenuesScreen.city`)}*</FormLabel>
      <Button
        className="d-block w-100 text-start form-control btn-input"
        onClick={() => setShowSearch(!showSearch)}
        style={{
          backgroundColor: colors.boxFill,
          borderColor: (cityMeta.touched && cityMeta.error) || (cityIdMeta.touched && cityIdMeta.error) ? colors.red : "transparent",
          color: colors.primaryText,
          textAlign: "left",
        }}
      >
        <div style={{ textAlign: "left", display: "inline-block" }}>{cityField.value ?? t("manageVenuesScreen.selectCity")}</div>
      </Button>

      {showSearch && (
        <div className="position-relative">
          <div
            className="position-absolute w-100"
            style={{ zIndex: 1000, border: `1px solid ${colors.stroke}`, borderRadius: "0.25rem", marginTop: "0.25rem", backgroundColor: colors.background }}
          >
            <FormGroup controlId={`city_search`} className="m-2">
              <FormControl
                type="text"
                placeholder={t("cityField.searchPlaceholder", "Search for a city...")}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                autoFocus
                className="textInput"
                style={{ backgroundColor: colors.boxFill, color: colors.primaryText }}
              />
            </FormGroup>
            {isLoadingCities && <div className="p-2 text-center"><Spinner animation="border" size="sm" /> {t("cityField.loading", "Loading...")}</div>}
            {citySearchApiError && <div className="p-2 text-danger">{t("cityField.searchError", "Error fetching cities.")}</div>}
            {!isLoadingCities && cityResults && cityResults.length === 0 && debouncedSearchValue.length >= 2 && (
              <div className="p-2 text-muted">{t("cityField.noResults", "No cities found.")}</div>
            )}
            {cityResults && cityResults.length > 0 && (
              <ListGroup variant="flush" style={{ maxHeight: "200px", overflowY: "auto" }}>
                {cityResults.map((city) => (
                  <ListGroupItem
                    key={city.id}
                    action
                    onClick={() => handleSelectCity(city)}
                    style={{
                      color: colors.primaryText,
                      display: "flex",
                      flexDirection: "column",
                    }}
                    className="city-search-item" // For hover/focus styles
                  >
                    <div style={{ margin: "0px", padding: "0px" }}>{city.name}</div>
                    <small style={{ margin: "0px", marginTop: "-4px", padding: "0px" }} className="text-muted">{city.region}</small>
                  </ListGroupItem>
                ))}
              </ListGroup>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CityField;
